/**
 * Byte-parity port of main-frontend `src/lib/utils/contrastColor.ts`
 * (the subset the runtime needs). The editor's Register section renders on
 * `getDarkBackdrop(palette)` when no explicit backgroundColor is stored —
 * the runtime must fall back to the SAME color or the section's baked-white
 * copy lands on the page background (audit 2026-07-20: invisible
 * white-on-cream "Register" heading on published sites).
 */
export function isColorDark(hex: string): boolean {
  const clean = hex.replace("#", "").slice(0, 6).padEnd(6, "0");
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance < 0.5;
}

function parseHex(hex: string): [number, number, number] {
  const clean = hex.replace("#", "").slice(0, 6).padEnd(6, "0");
  return [
    parseInt(clean.substring(0, 2), 16),
    parseInt(clean.substring(2, 4), 16),
    parseInt(clean.substring(4, 6), 16),
  ];
}

const toHex = (n: number) =>
  Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");

/** Linear blend of two hex colors: amt=0 → a, amt=1 → b. */
export function mix(a: string, b: string, amt: number): string {
  const [ar, ag, ab] = parseHex(a);
  const [br, bg, bb] = parseHex(b);
  const t = Math.max(0, Math.min(1, amt));
  return `#${toHex(ar + (br - ar) * t)}${toHex(ag + (bg - ag) * t)}${toHex(ab + (bb - ab) * t)}`;
}

/**
 * A dark, lightly brand-tinted backdrop used by the register section so it
 * reads as a dark branded panel on every theme. Dark themes keep their own
 * background; light themes get a near-black tinted toward the brand primary.
 */
export function getDarkBackdrop(palette: { primary: string; background: string }): string {
  if (isColorDark(palette.background)) return palette.background;
  return mix("#0f172a", palette.primary, 0.14);
}
